import React, { createContext, useContext } from "react";
import { ReadAgreementDetail } from "@/schemas/agreement";

export interface CommentItem {
  id: string;
  text: string;
  createdAt: string;
}

export type AgreementHandlerKey =
  | "complete"
  | "sendQuestionnaire"
  | "reject"
  | "confirm"
  | "comment"
  | "updateContent"
  | "updateData"
  | "updateStatements"
  | "updateTaxStatus"
  | "delete";

export type AgreementLoadingReason =
  | "fetching_agreement"
  | "sending_questionnaire"
  | "confirming_agreement"
  | "rejecting_agreement"
  | "requesting_comments"
  | "completing_agreement"
  | "updating_content"
  | "updating_data"
  | "updating_statements"
  | "updating_tax_status"
  | "deleting_agreement"
  | "unknown";

export type AgreementLoadingState =
  | { isLoading: false }
  | { isLoading: true; reason: AgreementLoadingReason };

export interface AgreementTransitions {
  canConfirm: boolean;
  canReject: boolean;
  canComment: boolean;
  canComplete: boolean;
  canSubmitForVerification: boolean;
  canPerformVerificationActions: boolean;
  canSendQuestionnaire: boolean;
  canUpdateContent: boolean;
  canUpdateData: boolean;
  canUpdateStatements: boolean;
  canUpdateTaxStatus: boolean;
  canDelete: boolean;
}

interface AgreementContextType {
  data?: ReadAgreementDetail;
  errorState: { error: Error | null; isQuery: boolean };
  loadingState: AgreementLoadingState;

  comments: CommentItem[];
  setComments: React.Dispatch<React.SetStateAction<CommentItem[]>>;

  // Transitions
  transitions: AgreementTransitions;

  // Simple validation
  hasValidComments: boolean;

  // Handlers
  handleSendQuestionnaire: () => void;
  handleComplete: (end_date: string) => void;
  handleReject: () => void;
  handleConfirm: () => void;
  handleComment: (comments?: CommentItem[]) => Promise<void>;
  handleUpdateContent: (amendment_date: string) => void;
  handleUpdateData: (amendment_date: string) => void;
  handleUpdateStatements: () => void;
  handleUpdateTaxStatus: (amendment_date: string) => void;
  handleDelete: () => void;
  clearError: () => void;
  refreshAgreement: () => void;
}

const AgreementContext = createContext<AgreementContextType>(
  {} as AgreementContextType,
);

export const useAgreementContext = () => {
  const context = useContext(AgreementContext);
  if (!context) {
    throw new Error(
      "useAgreementContext must be used within AgreementProvider",
    );
  }
  return context;
};

export { AgreementContext };
export type { AgreementContextType };
